// src/components/homepage_content/QuickSelection.js
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FootballAPI } from '../../services/footballApi';
import { useBetSlip } from '../../context/BetSlipContext';
import MatchCard from '../sports/MatchCard';

const QuickSelection = () => {
  const navigate = useNavigate();
  const { addToBetSlip, bets } = useBetSlip();
  
  const [selectedDate, setSelectedDate] = useState('today');
  const [selectedSport, setSelectedSport] = useState('football');
  const [selectedBetType, setSelectedBetType] = useState('match_winner');
  const [selectedBets, setSelectedBets] = useState([]);
  const [totalOdds, setTotalOdds] = useState(1.0);
  const [groupedMatches, setGroupedMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [oddsFormat, setOddsFormat] = useState('decimal');
  const [viewMode, setViewMode] = useState('grid');
  
  const betTypesRef = useRef(null);

  // League priority order (highest priority first)
  const getLeaguePriority = (leagueId, leagueName) => {
    const priorityMap = {
      1: 1,   // FIFA World Cup
      2: 2,   // UEFA Champions League
      39: 3,  // Premier League
      140: 4, // La Liga
      135: 5, // Serie A
      78: 6,  // Bundesliga
      61: 7,  // Ligue 1
    };
    
    if (priorityMap[leagueId]) {
      return priorityMap[leagueId];
    }
    
    const name = leagueName?.toLowerCase() || '';
    if (name.includes('fifa world cup') || name.includes('world cup')) return 1;
    if (name.includes('champions league')) return 2;
    if (name.includes('premier league')) return 3;
    if (name.includes('la liga')) return 4;
    if (name.includes('serie a')) return 5;
    if (name.includes('bundesliga')) return 6;
    if (name.includes('ligue 1')) return 7;
    if (name.includes('eredivisie')) return 8;
    if (name.includes('primeira liga')) return 9;
    if (name.includes('mls')) return 10;
    if (name.includes('usl championship')) return 11;
    if (name.includes('usl league one')) return 12;
    if (name.includes('usl league two')) return 13;
    
    return 999;
  };

  // Date options
  const dates = useMemo(() => [
    { id: 'today', label: 'Today', icon: '📅', days: 0 },
    { id: 'tomorrow', label: 'Tomorrow', icon: '⏭️', days: 1 },
    { id: 'weekend', label: 'This Weekend', icon: '🎉', days: 'weekend' }
  ], []);

  // Sports options
  const sports = useMemo(() => [
    { id: 'football', name: 'Football', icon: '⚽', color: 'from-blue-500 to-cyan-400' },
    { id: 'basketball', name: 'Basketball', icon: '🏀', color: 'from-orange-500 to-red-500', comingSoon: true },
    { id: 'tennis', name: 'Tennis', icon: '🎾', color: 'from-green-500 to-emerald-400', comingSoon: true },
    { id: 'cricket', name: 'Cricket', icon: '🏏', color: 'from-yellow-500 to-amber-400', comingSoon: true },
    { id: 'baseball', name: 'Baseball', icon: '⚾', color: 'from-red-500 to-pink-500', comingSoon: true }
  ], []);

  // Bet types with their API market mapping
  const betTypes = useMemo(() => [
    { id: 'match_winner', name: 'Match Winner', short: '1X2', icon: '🏆', marketType: 'match_winner' },
    { id: 'double_chance', name: 'Double Chance', short: 'DC', icon: '🔒', marketType: 'double_chance' },
    { id: 'over_under', name: 'Over/Under', short: 'O/U', icon: '📊', marketType: 'over_under' },
    { id: 'btts', name: 'Both Teams Score', short: 'BTTS', icon: '⚽⚽', marketType: 'btts' },
    { id: 'first_half', name: '1st Half', short: '1H', icon: '⏱️', marketType: 'first_half_winner' },
    { id: 'second_half', name: '2nd Half', short: '2H', icon: '⏱️', marketType: 'second_half_winner' }
  ], []);

  // Get date string based on selection
  const getDateString = useCallback((dateId) => {
    const today = new Date();
    if (dateId === 'today') {
      return today.toISOString().split('T')[0];
    } else if (dateId === 'tomorrow') {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split('T')[0];
    } else if (dateId === 'weekend') {
      // Get next Saturday
      const saturday = new Date(today);
      saturday.setDate(today.getDate() + ((6 - today.getDay() + 7) % 7));
      return saturday.toISOString().split('T')[0];
    }
    return today.toISOString().split('T')[0];
  }, []);

  // Fetch matches from API
  const fetchMatches = useCallback(async () => {
    setLoading(true);
    try {
      const dateStr = getDateString(selectedDate);
      const response = await FootballAPI.getFixtures(dateStr);
      
      if (response.success && response.data) {
        // Filter matches based on selected bet type
        let filteredData = response.data;
        
        // Only show matches that have odds for the selected bet type
        if (selectedBetType) {
          filteredData = response.data.map(group => ({
            ...group,
            fixtures: group.fixtures.filter(fixture => {
              // Check if this fixture has odds for the selected bet type
              const oddsMap = {
                'match_winner': fixture.odds?.home || fixture.odds?.draw || fixture.odds?.away,
                'double_chance': fixture.double_chance_odds?.home_draw || fixture.double_chance_odds?.home_away || fixture.double_chance_odds?.draw_away,
                'over_under': fixture.over_under_odds?.over || fixture.over_under_odds?.under,
                'btts': fixture.btts_odds?.yes || fixture.btts_odds?.no,
                'first_half': fixture.first_half_odds?.home || fixture.first_half_odds?.draw || fixture.first_half_odds?.away,
                'second_half': fixture.second_half_odds?.home || fixture.second_half_odds?.draw || fixture.second_half_odds?.away
              };
              return !!oddsMap[selectedBetType];
            })
          })).filter(group => group.fixtures.length > 0);
        }
        
        // Sort leagues by priority (top leagues first)
        filteredData.sort((a, b) => {
          const priorityA = getLeaguePriority(a.league_id, a.league_name);
          const priorityB = getLeaguePriority(b.league_id, b.league_name);
          return priorityA - priorityB;
        });
        
        setGroupedMatches(filteredData);
      }
    } catch (error) {
      // console.error('Failed to fetch matches:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedBetType, getDateString]);

  useEffect(() => {
    if (selectedSport === 'football') {
      fetchMatches();
    }
  }, [selectedSport, selectedDate, selectedBetType, fetchMatches]);

  // Flatten matches for stats
  const allMatches = groupedMatches.flatMap(group => group.fixtures);
  const liveMatches = allMatches.filter(m => ['1H', 'HT', '2H', 'ET', 'P'].includes(m.status));

  const handleDateSelect = useCallback((dateId) => {
    setSelectedDate(dateId);
    setSelectedBets([]);
  }, []);

  const handleSportSelect = useCallback((sportId) => {
    setSelectedSport(sportId);
    setSelectedBets([]);
  }, []);

  const handleBetTypeSelect = useCallback((betTypeId) => {
    setSelectedBetType(betTypeId);
    setSelectedBets([]);
  }, []);

  const handleMatchCardClick = (matchId) => {
    navigate(`/match/${matchId}`);
  };

  const handleAddToBetslip = useCallback(() => {
    if (selectedBets.length === 0) return;
    
    selectedBets.forEach(bet => {
      addToBetSlip({
        matchId: bet.matchId,
        match: bet.matchName,
        selection: bet.selection,
        odds: bet.odds,
        marketType: bet.marketType,
        sport: bet.sport
      });
    });
    
    setSelectedBets([]);
    
    // Show success feedback
    const toast = document.createElement('div');
    toast.className = 'fixed bottom-24 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-xl z-50 animate-slide-up';
    toast.innerHTML = `✓ ${selectedBets.length} bet(s) added to betslip!`;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  }, [selectedBets, addToBetSlip]);

  const handleClearBets = useCallback(() => {
    setSelectedBets([]);
  }, []);

  // Update total odds when selected bets change
  useEffect(() => {
    if (selectedBets.length > 0) {
      const calculatedOdds = selectedBets.reduce((acc, bet) => acc * bet.odds, 1.0);
      setTotalOdds(parseFloat(calculatedOdds.toFixed(2)));
    } else {
      setTotalOdds(1.0);
    }
  }, [selectedBets]);

  const scrollBetTypes = (direction) => {
    if (betTypesRef.current) {
      const scrollAmount = 200;
      betTypesRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Get top leagues with special badges
  const isTopLeague = (leagueId, leagueName) => {
    const priority = getLeaguePriority(leagueId, leagueName);
    return priority <= 7;
  };

  const isWorldCup = (leagueId, leagueName) => {
    return leagueId === 1 || leagueName?.toLowerCase().includes('world cup');
  };

  // Loading state
  if (loading && selectedSport === 'football') {
    return (
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-900 to-blue-900/20 rounded-2xl p-6 border border-gray-800 shadow-xl">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            <p className="text-gray-400 mt-4">Loading matches...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section 
      className="relative bg-gradient-to-br from-gray-900 via-gray-900 to-blue-900/20 rounded-2xl p-6 border border-gray-800 shadow-xl overflow-hidden"
      aria-labelledby="quick-bet-heading"
    >
      {/* Animated Background Elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-cyan-500 to-blue-500 animate-gradient-x" />
      <div className="absolute -top-10 -right-10 w-40 h-40 bg-blue-500/5 rounded-full blur-3xl" />
      
      <div className="relative">
        {/* Header Section */}
        <div className="mb-8 text-center md:text-left">
          <div className="inline-flex items-center gap-3 mb-3">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <h2 
              id="quick-bet-heading"
              className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-white to-blue-300 bg-clip-text text-transparent"
            >
              Quick Bet
            </h2>
            <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-xs font-bold rounded-full">
              LIVE ODDS
            </span>
          </div>
          <p className="text-gray-400 text-sm md:text-base">Real-time odds from Bet365 • Select matches and place bets quickly</p>
        </div>

      
        {/* Bet Type Selection */}
        

        {/* View Mode Toggle */}
        {selectedSport === 'football' && allMatches.length > 0 && (
          <div className="flex items-center justify-end gap-2 mb-4">
            <span className="text-xs text-gray-500">View:</span>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-blue-600' : 'bg-gray-700'}`}
              aria-label="Grid view"
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-blue-600' : 'bg-gray-700'}`}
              aria-label="List view"
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        )}

        {/* Matches Grid - Using MatchCard Component */}
        {selectedSport === 'football' && (
          <div className="space-y-6">
            {groupedMatches.length === 0 && !loading ? (
              <div className="text-center py-12 bg-gray-800/50 rounded-xl">
                <div className="text-5xl mb-4">⚽</div>
                <h3 className="text-white font-bold text-lg mb-2">No matches available</h3>
                <p className="text-gray-400 text-sm">Try selecting a different date or bet type</p>
              </div>
            ) : (
              groupedMatches.map((group) => {
                const isWorldCupLeague = isWorldCup(group.league_id, group.league_name);
                const isTop = isTopLeague(group.league_id, group.league_name);
                
                return (
                  <div key={group.league_id} className="space-y-3">
                    {/* League Header with Priority Badge */}
                    <div className="flex items-center gap-2 bg-gray-800/50 rounded-lg p-3">
                      {group.league_logo && (
                        <img src={group.league_logo} alt={group.league_name} className="w-5 h-5 object-contain" />
                      )}
                      <h3 className="font-bold text-white text-sm">{group.league_name}</h3>
                      <span className="text-xs text-gray-400 bg-gray-700 px-2 py-0.5 rounded-full">
                        {group.fixtures.length}
                      </span>
                      
                      {/* Priority Badges */}
            
                    </div>
                    
                    {/* Matches Grid */}
                    <div className={viewMode === 'grid' 
                      ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" 
                      : "space-y-3"
                    }>
                      {group.fixtures.map((match) => {
                        // Attach selected bet type odds to match for display
                        const matchWithOdds = {
                          ...match,
                          selectedBetType: selectedBetType,
                          onBetSelect: (matchId, betType, oddData) => {
                            const betKey = `${matchId}-${betType}-${oddData.label}`;
                            const betTypeObj = betTypes.find(bt => bt.id === betType);
                            
                            setSelectedBets(prev => {
                              const isSelected = prev.some(bet => bet.key === betKey);
                              if (isSelected) {
                                return prev.filter(bet => bet.key !== betKey);
                              } else {
                                return [...prev, {
                                  key: betKey,
                                  matchId: matchId,
                                  matchName: `${match.home_team} vs ${match.away_team}`,
                                  selection: oddData.outcome,
                                  odds: parseFloat(oddData.value),
                                  marketType: betTypeObj?.marketType || betType,
                                  sport: 'football'
                                }];
                              }
                            });
                          }
                        };
                        
                        return (
                          <div key={match.id} onClick={() => handleMatchCardClick(match.id)}>
                            <MatchCard
                              match={matchWithOdds}
                              sport="football"
                              oddsFormat={oddsFormat}
                              viewMode={viewMode}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Other Sports Coming Soon */}
        {selectedSport !== 'football' && (
          <div className="text-center py-12 bg-gray-800/50 rounded-xl">
            <div className="text-5xl mb-4">{sports.find(s => s.id === selectedSport)?.icon}</div>
            <h3 className="text-white font-bold text-lg mb-2">
              {selectedSport?.charAt(0).toUpperCase() + selectedSport?.slice(1)} Coming Soon
            </h3>
            <p className="text-gray-400 text-sm">We're working hard to bring you {selectedSport} betting options</p>
          </div>
        )}

        {/* Selected Bets Summary */}
        {selectedBets.length > 0 && (
          <div className="sticky bottom-4 mt-8 p-5 bg-gradient-to-r from-gray-800 to-gray-900 border border-blue-500/30 rounded-xl shadow-xl animate-slide-up z-50">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                  <h3 className="text-white font-bold text-lg">
                    {selectedBets.length} Selection{selectedBets.length > 1 ? 's' : ''}
                  </h3>
                </div>
                <div className="text-gray-400 text-sm">Ready to add to betslip</div>
                
                <div className="mt-4 flex items-center gap-4">
                  <div className="bg-blue-500/20 px-4 py-2 rounded-lg">
                    <div className="text-xs text-blue-400">Total Odds</div>
                    <div className="text-white font-bold text-xl">{totalOdds}</div>
                  </div>
                  <div className="text-sm text-gray-400">
                    Potential Win: <span className="text-green-400 font-bold">${(10 * totalOdds).toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button 
                  onClick={handleClearBets}
                  className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-semibold transition-colors"
                >
                  🗑️ Clear
                </button>
                <button 
                  onClick={handleAddToBetslip}
                  className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white rounded-xl font-bold transition-all shadow-lg hover:shadow-green-500/30"
                >
                  🎯 Add to Betslip
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default QuickSelection;